import { createContext, useContext, useEffect, useState, type PropsWithChildren } from 'react'

/*
 * `any-hover`, not `hover`: the latter reflects whichever input the OS currently treats as
 * primary, which flips on hybrid devices (touchscreen laptops, iPad + trackpad) even though a
 * hover-capable input is still attached. `any-hover: none` only matches when nothing connected
 * can hover — the actual condition under which hover-driven UI (tooltips) is unreachable.
 */
const TOUCH_MEDIA_QUERY = '(any-hover: none)'

export const TouchDeviceContext = createContext(false)

export function useTouchDevice() {
  return useContext(TouchDeviceContext)
}

export function TouchDeviceProvider({ children }: PropsWithChildren) {
  const [isTouchDevice, setIsTouchDevice] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia(TOUCH_MEDIA_QUERY)
    const handleChange = (event: MediaQueryListEvent) => setIsTouchDevice(event.matches)

    setIsTouchDevice(mediaQuery.matches)

    // Safari 12 (in this app's browserslist target) predates MediaQueryList.addEventListener.
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }

    mediaQuery.addListener(handleChange)
    return () => mediaQuery.removeListener(handleChange)
  }, [])

  return <TouchDeviceContext.Provider value={isTouchDevice}>{children}</TouchDeviceContext.Provider>
}
